import { useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { Product } from "../../appData/productData"
import { formatCurrency } from "../../utils/utils"
import styles from "./ReceiptPaywall.module.scss"

/**
 * ReceiptPaywall (/receipt) — an original, self-contained paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md). The prior paywalls
 * clustered on PRIVACY pitched as a DARK + SKEUOMORPHIC, centered-hero +
 * one-glowing-card screen. Every axis moves here:
 *   - Value frame:   BACKUP-RESILIENCE — "nothing gets lost." Untouched by
 *                    every prior paywall (none pitched resilience/restore).
 *   - Layout:        a RECEIPT / itemized manifest. No hero, no card, no
 *                    perk grid. The whole screen is a printed thermal receipt.
 *   - Interaction:   INPUT-DRIVEN via a YEARLY/MONTHLY toggle that re-prints
 *                    the TOTAL line, plus a one-time count-up tally as the
 *                    receipt "prints."
 *   - Register:      DEADPAN / wry — a receipt is the driest object there is.
 *   - Art direction: BRUTALIST monospace on a LIGHT paper ground. Not dark,
 *                    not skeuomorphic-metal, no particles, no glow.
 *
 * The yearly price is the bold TOTAL; monthly is shown smaller, and choosing
 * it reveals the (real, computed) annualized cost so yearly stays the deal.
 */

// Illustrative figure for the "PHOTOS BACKED UP" tally (debug numPhotos is 0;
// the receipt is a marketing artifact, so a representative count is used).
const PHOTOS_TALLY = 12480

interface LineItem {
  label: string
  value: string
}

// The manifest — every line is about RESILIENCE: losing the phone, not the
// photos. Deliberately reads like a store receipt, not a feature list.
const ITEMS: LineItem[] = [
  { label: "AUTO-BACKUP", value: "ON" },
  { label: "DEVICES COVERED", value: "ALL" },
  { label: "FULL RESOLUTION", value: "KEPT" },
  { label: "PHONE LOST / STOLEN", value: "COVERED" },
  { label: "CRACKED / DROPPED / GONE", value: "COVERED" },
  { label: "RESTORE TO NEW PHONE", value: "1 TAP" },
  { label: "STORAGE", value: "UNLIMITED" },
]

const ReceiptPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPriceNum = yearlyProduct?.price ?? 35.99
  const monthlyPriceNum = monthlyProduct?.price ?? 13.99

  const yearlyPrice = formatCurrency(yearlyPriceNum, locale)
  const yearlyPerMonth = formatCurrency(yearlyPriceNum / 12, locale)
  const monthlyPrice = formatCurrency(monthlyPriceNum, locale)
  const monthlyAnnualized = formatCurrency(monthlyPriceNum * 12, locale)
  const savings = formatCurrency(
    Math.max(0, monthlyPriceNum * 12 - yearlyPriceNum),
    locale,
  )
  const savingsPct = Math.round(
    (1 - yearlyPriceNum / (monthlyPriceNum * 12)) * 100,
  )

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── Print reveal: feed the receipt out one line at a time ───────────
  const totalSteps = 6 + ITEMS.length // header rows + item rows
  const [printed, setPrinted] = useState(reduceMotion ? totalSteps : 0)
  useEffect(() => {
    if (reduceMotion) return
    let n = 0
    const id = setInterval(() => {
      n += 1
      setPrinted(n)
      if (n >= totalSteps) clearInterval(id)
    }, 130)
    return () => clearInterval(id)
  }, [reduceMotion, totalSteps])

  // ── Count-up tally, runs once while the receipt prints ──────────────
  const [tally, setTally] = useState(reduceMotion ? PHOTOS_TALLY : 0)
  useEffect(() => {
    if (reduceMotion) return
    const start = performance.now()
    const dur = 1400
    let raf = 0
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur)
      const eased = 1 - Math.pow(1 - p, 3)
      setTally(Math.round(eased * PHOTOS_TALLY))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [reduceMotion])

  const orderNo = useMemo(() => {
    // Stable per-mount order number (no Date.now / random in render path).
    const base = Math.round((yearlyPriceNum + monthlyPriceNum) * 1000)
    return (base % 90000) + 10000
  }, [yearlyPriceNum, monthlyPriceNum])

  const handleClose = () => native.postNativeEvent("close")
  const handlePurchase = () => {
    const product: Product | undefined =
      plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }

  const selectedHasTrial =
    plan === "yearly"
      ? (yearlyProduct?.hasTrial ?? true)
      : (monthlyProduct?.hasTrial ?? false)

  // Print step where each row becomes visible.
  const shown = (step: number) => printed >= step

  return (
    <div className={styles.page}>
      <button className={styles.close} onClick={handleClose} aria-label="Close">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
          <path
            d="M6 6l12 12M18 6L6 18"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {/* The slot the paper feeds out of */}
      <div className={styles.slot} aria-hidden />

      <motion.div
        className={styles.receipt}
        initial={reduceMotion ? false : { y: -14, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 240, damping: 26 }}
      >
        {/* torn top edge */}
        <div className={`${styles.edge} ${styles.edgeTop}`} aria-hidden />

        <div className={styles.body}>
          {/* ── Brand header ───────────────────────────────── */}
          <div className={styles.brand}>
            <span className={styles.mark}>BACKUP&nbsp;CO.</span>
            <span className={styles.tagline}>· PROOF OF RESILIENCE ·</span>
            <span className={styles.addr}>
              EVERY PHOTO — EVERY DEVICE — NOTHING LOST
            </span>
          </div>

          <div className={styles.rule} aria-hidden />

          {/* ── Order meta ─────────────────────────────────── */}
          <div className={styles.meta}>
            <span>ORDER #{orderNo}</span>
            <span>CASHIER: AUTO-BACKUP</span>
          </div>

          {/* ── Headline tally ─────────────────────────────── */}
          <div className={styles.tallyRow}>
            <span className={styles.tallyLabel}>PHOTOS BACKED UP</span>
            <span className={styles.tallyNum}>
              {tally.toLocaleString("en-US")}
            </span>
          </div>

          <div className={styles.ruleDashed} aria-hidden />

          {/* ── Itemized manifest ──────────────────────────── */}
          <div className={styles.items}>
            {ITEMS.map((it, i) => (
              <div
                key={it.label}
                className={`${styles.item} ${
                  shown(6 + i) ? styles.itemIn : ""
                }`}
              >
                <span className={styles.itemLabel}>{it.label}</span>
                <span className={styles.dots} aria-hidden />
                <span className={styles.itemValue}>{it.value}</span>
              </div>
            ))}
          </div>

          <div className={styles.ruleDashed} aria-hidden />

          {/* ── Plan toggle ────────────────────────────────── */}
          <div className={styles.planRow}>
            <span className={styles.planLabel}>BILLING</span>
            <div
              className={styles.toggle}
              role="tablist"
              aria-label="Choose billing period"
            >
              <button
                role="tab"
                aria-selected={plan === "yearly"}
                className={`${styles.toggleBtn} ${
                  plan === "yearly" ? styles.toggleOn : ""
                }`}
                onClick={() => setPlan("yearly")}
              >
                YEARLY
              </button>
              <button
                role="tab"
                aria-selected={plan === "monthly"}
                className={`${styles.toggleBtn} ${
                  plan === "monthly" ? styles.toggleOn : ""
                }`}
                onClick={() => setPlan("monthly")}
              >
                MONTHLY
              </button>
            </div>
          </div>

          {/* ── Totals block ───────────────────────────────── */}
          {plan === "yearly" ? (
            <div className={styles.totals}>
              <div className={styles.subline}>
                <span>1 YR UNLIMITED BACKUP</span>
                <span>{yearlyPrice}</span>
              </div>
              <div className={styles.subline}>
                <span>BILLED ANNUALLY</span>
                <span>{yearlyPerMonth}/MO</span>
              </div>
              <div className={styles.totalLine}>
                <span>TOTAL</span>
                <span className={styles.totalPrice}>{yearlyPrice}</span>
              </div>
              <div className={styles.perYr}>PER YEAR</div>
              {savingsPct > 0 && (
                <div className={styles.saved}>
                  ★ YOU SAVE {savings} ({savingsPct}%) vs PAYING MONTHLY
                </div>
              )}
            </div>
          ) : (
            <div className={styles.totals}>
              <div className={styles.subline}>
                <span>1 MO UNLIMITED BACKUP</span>
                <span>{monthlyPrice}</span>
              </div>
              <div className={styles.subline}>
                <span>× 12 MONTHS</span>
                <span className={styles.strike}>{monthlyAnnualized}/YR</span>
              </div>
              <div className={styles.totalLine}>
                <span>TOTAL</span>
                <span className={styles.totalPrice}>{monthlyPrice}</span>
              </div>
              <div className={styles.perYr}>PER MONTH</div>
              {savingsPct > 0 && (
                <button
                  className={styles.switchBack}
                  onClick={() => setPlan("yearly")}
                >
                  ↑ SWITCH TO YEARLY — SAVE {savings}
                </button>
              )}
            </div>
          )}

          <div className={styles.rule} aria-hidden />

          {/* ── Thanks + barcode ───────────────────────────── */}
          <div className={styles.thanks}>** KEEP THIS RECEIPT FOREVER **</div>
          <div className={styles.barcode} aria-hidden>
            {BARS.map((w, i) => (
              <span key={i} style={{ width: w }} />
            ))}
          </div>
          <div className={styles.barcodeNo}>
            {plan === "yearly" ? "Y" : "M"} {orderNo}{" "}
            {String(orderNo).split("").reverse().join("")}
          </div>
        </div>

        {/* ── Perforation + tear-off CTA stub ──────────────── */}
        <div className={styles.perf} aria-hidden>
          <span className={styles.perfText}>— — — TEAR HERE — — —</span>
        </div>

        <button className={styles.cta} onClick={handlePurchase}>
          <span className={styles.ctaTop}>
            {selectedHasTrial && plan === "yearly"
              ? "START FREE — THEN ACTIVATE BACKUP"
              : "ACTIVATE MY BACKUP"}
          </span>
          <span className={styles.ctaPrice}>
            {plan === "yearly"
              ? `${yearlyPrice} / YEAR`
              : `${monthlyPrice} / MONTH`}
          </span>
        </button>

        <div className={styles.fine}>
          {plan === "yearly" && selectedHasTrial
            ? "Free trial, then renews yearly. Cancel anytime."
            : "Auto-renews until cancelled. Cancel anytime."}{" "}
          Your photos are always yours, even if you cancel.
        </div>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>

        <div className={`${styles.edge} ${styles.edgeBottom}`} aria-hidden />
      </motion.div>
    </div>
  )
}

// Pseudo-barcode bar widths (px). Fixed pattern — no randomness in render.
const BARS = [
  3, 1, 2, 4, 1, 1, 3, 2, 1, 5, 1, 2, 1, 3, 2, 4, 1, 1, 2, 3, 1, 2, 1, 4, 2, 1,
  3, 1, 2, 1, 5, 1, 2, 3, 1, 2, 4, 1, 1, 2, 3, 1,
].map((n) => `${n}px`)

export default ReceiptPaywall
